/**
 * Настройки вида отдельных фигур — у прямоугольника и регрессии.
 *
 * У Фибоначчи и позиции свои большие наборы; здесь — то немногое, что
 * есть у простых фигур: средняя линия прямоугольника и её цвет, цвета
 * верхней и нижней половин канала регрессии, его ширина и подпись
 * силы тренда.
 */

import type { DrawingKind } from './kinds.js';

export interface ShapeOptions {
  /** Прямоугольник: пунктир посередине — цена середины зоны. */
  readonly midline: boolean;
  readonly midlineColor: string;
  /** Регрессия: цвет верхней половины канала — там цена дорогая. */
  readonly upperColor: string;
  /** Регрессия: цвет нижней половины — там цена дешёвая. */
  readonly lowerColor: string;
  /** Регрессия: ширина канала в стандартных отклонениях. */
  readonly deviation: number;
  /** Регрессия: подпись силы тренда (коэффициент корреляции). */
  readonly showR: boolean;
}

export const DEFAULT_SHAPE_OPTIONS: ShapeOptions = {
  midline: false,
  midlineColor: '#EEF3FA',
  upperColor: '#FF5470',
  lowerColor: '#2EE6B0',
  deviation: 2,
  showR: true,
};

const WITH_OPTIONS = new Set<DrawingKind>(['rect', 'regression']);

/** Есть ли у фигуры эти настройки — по ним окно показывает вкладку «Стиль». */
export function hasShapeOptions(kind: DrawingKind): boolean {
  return WITH_OPTIONS.has(kind);
}

export function shapeOptionsOf(stored: Partial<ShapeOptions> | undefined): ShapeOptions {
  return stored ? { ...DEFAULT_SHAPE_OPTIONS, ...stored } : DEFAULT_SHAPE_OPTIONS;
}

const HEX = /^#([0-9a-f]{6}|[0-9a-f]{8})$/i;

/** Приведение сохранённого: чужие и битые значения заменяются умолчаниями. */
export function normalizeShapeOptions(value: unknown): ShapeOptions | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const raw = value as Partial<ShapeOptions>;
  const color = (entry: unknown, fallback: string): string => (typeof entry === 'string' && HEX.test(entry) ? entry : fallback);
  return {
    midline: raw.midline === true,
    midlineColor: color(raw.midlineColor, DEFAULT_SHAPE_OPTIONS.midlineColor),
    upperColor: color(raw.upperColor, DEFAULT_SHAPE_OPTIONS.upperColor),
    lowerColor: color(raw.lowerColor, DEFAULT_SHAPE_OPTIONS.lowerColor),
    deviation:
      typeof raw.deviation === 'number' && raw.deviation >= 0.5 && raw.deviation <= 4 ? raw.deviation : DEFAULT_SHAPE_OPTIONS.deviation,
    showR: raw.showR !== false,
  };
}
